import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { ApiError, api } from '../lib/api';
import { Alert, Button, Card, Label, TextInput } from '../components/ui';

export default function LoginPage() {
  const { t } = useTranslation();
  const { user, login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'password' | 'mfa'>('password');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [azureSsoEnabled, setAzureSsoEnabled] = useState(false);
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('sso_error');

  useEffect(() => {
    api
      .getAppConfig()
      .then((c) => setAzureSsoEnabled(c.azureSsoEnabled))
      .catch(() => undefined);
  }, []);

  if (user && !user.mustChangePassword) return <Navigate to="/" replace />;
  if (user) return <Navigate to="/change-password" replace />;

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await login(email, password);
      if ('mfaRequired' in result) {
        setStep('mfa');
        setCode('');
        return;
      }
      navigate(result.user.mustChangePassword ? '/change-password' : '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await verifyMfa(code.trim());
      navigate(me.mustChangePassword ? '/change-password' : '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const backToPassword = () => {
    setStep('password');
    setCode('');
    setUseRecovery(false);
    setError(null);
  };

  return (
    <div className="mx-auto flex max-w-md flex-col gap-6 px-6 py-20">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          {step === 'password' ? t('auth.loginTitle') : t('auth.mfa.title')}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {step === 'password' ? t('app.tagline') : t('auth.mfa.loginHint')}
        </p>
      </div>
      <Card>
        {ssoError && (
          <div className="mb-4">
            <Alert kind="error">{t('login.ssoError')}</Alert>
          </div>
        )}
        {step === 'password' ? (
          <>
          <form onSubmit={submitPassword} className="flex flex-col gap-4">
            {error && <Alert kind="error">{error}</Alert>}
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <TextInput
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <TextInput
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:text-slate-200"
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 3l18 18" />
                      <path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-3.2 4.1M6.1 6.1A18 18 0 0 0 2 12s3.5 7 10 7a10.9 10.9 0 0 0 4-.8" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={busy}>
              {busy ? t('common.pleaseWait') : t('auth.login')}
            </Button>
          </form>
          {azureSsoEnabled && (
            <a
              href="/api/auth/sso/login"
              className="mt-3 block w-full rounded border border-slate-300 px-4 py-2 text-center text-sm font-medium hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800"
            >
              {t('login.withMicrosoft')}
            </a>
          )}
          </>
        ) : (
          <form onSubmit={submitMfa} className="flex flex-col gap-4">
            {error && <Alert kind="error">{error}</Alert>}
            <div>
              <Label htmlFor="mfa-code">
                {useRecovery ? t('auth.mfa.recoveryLabel') : t('auth.mfa.codeLabel')}
              </Label>
              <TextInput
                id="mfa-code"
                autoFocus
                autoComplete="one-time-code"
                inputMode={useRecovery ? 'text' : 'numeric'}
                pattern={useRecovery ? undefined : '[0-9]*'}
                maxLength={useRecovery ? 32 : 6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {useRecovery && (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('auth.mfa.recoveryExplain')}</p>
              )}
            </div>
            <Button type="submit" disabled={busy}>
              {busy ? t('common.pleaseWait') : t('auth.mfa.verify')}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                className="text-slate-600 underline-offset-2 hover:underline dark:text-slate-300"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode('');
                }}
              >
                {useRecovery ? t('auth.mfa.useTotp') : t('auth.mfa.useRecovery')}
              </button>
              <button
                type="button"
                className="text-slate-600 underline-offset-2 hover:underline dark:text-slate-300"
                onClick={backToPassword}
              >
                {t('auth.mfa.back')}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
